/** CircuitsTab — circuit schedule editor + CSV import/export. */
import { useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Upload, Download } from "@phosphor-icons/react";
import { LOAD_TYPES, newCircuit, downloadFile } from "./_shared";

export default function CircuitsTab({ project, update, projectId }) {
  const fileRef = useRef();
  const circuits = project.circuits || [];

  const setCircuits = (next) => update({ circuits: next });
  const add = () => setCircuits([...circuits, newCircuit()]);
  const edit = (i, patch) => {
    const next = [...circuits];
    next[i] = { ...next[i], ...patch };
    setCircuits(next);
  };
  const del = (i) => setCircuits(circuits.filter((_, idx) => idx !== i));

  const uploadCsv = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/projects/${projectId}/csv-import`, form);
      const { data } = await api.get(`/projects/${projectId}`);
      update({ circuits: data.circuits });
      toast.success(`Imported circuits from ${file.name}`);
    } catch {
      toast.error("CSV import failed");
    }
  };

  const downloadCircuitsCsv = async (path, suffix) => {
    try {
      const resp = await fetch(`${API}/projects/${projectId}/circuits/${path}`, { credentials: "include" });
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(project.name || "panel").replace(/[^A-Za-z0-9_-]+/g, "_")}_${suffix}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(suffix === "circuits_template" ? "Template downloaded" : "Circuits CSV exported");
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-tag">// 02 Circuit schedule</div>
          <h2 className="font-display text-2xl font-bold">Circuits & loads</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv" className="hidden"
            onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])} data-testid="csv-input" />
          <button onClick={() => downloadCircuitsCsv("csv-template", "circuits_template")} className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="download-circuits-template">
            <Download size={16} /> Template
          </button>
          <button onClick={() => fileRef.current?.click()} className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="upload-csv">
            <Upload size={16} /> Import CSV
          </button>
          <button onClick={() => downloadCircuitsCsv("csv-export", "circuits")} className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="export-circuits-csv" disabled={circuits.length === 0}>
            <Download size={16} /> Export CSV
          </button>
          <button
            onClick={async () => {
              try {
                await downloadFile(`/projects/${projectId}/circuits/xlsx`, `${(project.name || "panel").replace(/[^A-Za-z0-9_-]+/g, "_")}_circuits.xlsx`);
                toast.success("XLSX exported");
              } catch { toast.error("XLSX export failed"); }
            }}
            className="btn-ghost text-sm py-2 inline-flex items-center gap-2" data-testid="export-circuits-xlsx" disabled={circuits.length === 0}>
            <Download size={16} /> Export XLSX
          </button>
          <button onClick={add} className="btn-primary text-sm py-2 inline-flex items-center gap-2" data-testid="add-circuit">
            <Plus size={16} weight="bold" /> Add circuit
          </button>
        </div>
      </div>

      <div className="text-xs text-gray-500 font-mono mb-3">
        CSV columns: <span className="text-[#00E5FF]">tag, description, load_type, voltage, phases, load_amps, breaker_amps, poles, notes</span> ·
        <span className="text-gray-400"> download the template, fill in your loads, then re-import.</span>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full" data-testid="circuits-table">
          <thead className="bg-[#1A1F29]">
            <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono text-left">
              <th className="p-2">#</th><th className="p-2">Tag</th><th className="p-2">Description</th><th className="p-2">Type</th>
              <th className="p-2">V</th><th className="p-2">φ</th><th className="p-2">A (load)</th><th className="p-2">Brkr A</th><th className="p-2">P</th><th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {circuits.map((c, i) => (
              <tr key={c.id || i} className="border-t border-white/5 font-mono text-sm" data-testid={`circuit-row-${i}`}>
                <td className="p-2 text-gray-500">{String(i + 1).padStart(2, "0")}</td>
                <td className="p-2"><input value={c.tag} onChange={(e) => edit(i, { tag: e.target.value })} className="bg-black/40 border border-white/10 px-2 py-1 w-20 focus:border-[#00E5FF] outline-none" /></td>
                <td className="p-2"><input value={c.description} onChange={(e) => edit(i, { description: e.target.value })} className="bg-black/40 border border-white/10 px-2 py-1 w-full min-w-[180px] focus:border-[#00E5FF] outline-none" /></td>
                <td className="p-2">
                  <select value={c.load_type} onChange={(e) => edit(i, { load_type: e.target.value })} className="bg-black/40 border border-white/10 px-2 py-1 focus:border-[#00E5FF] outline-none">
                    {LOAD_TYPES.map((lt) => <option key={lt} value={lt}>{lt.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td className="p-2"><input type="number" value={c.voltage} onChange={(e) => edit(i, { voltage: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-16 focus:border-[#00E5FF] outline-none" /></td>
                <td className="p-2"><input type="number" value={c.phases} onChange={(e) => edit(i, { phases: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-12 focus:border-[#00E5FF] outline-none" /></td>
                <td className="p-2"><input type="number" value={c.load_amps} onChange={(e) => edit(i, { load_amps: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-16 focus:border-[#00E5FF] outline-none" /></td>
                <td className="p-2"><input type="number" value={c.breaker_amps} onChange={(e) => edit(i, { breaker_amps: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-16 focus:border-[#00E5FF] outline-none" /></td>
                <td className="p-2"><input type="number" value={c.poles} onChange={(e) => edit(i, { poles: Number(e.target.value) })} className="bg-black/40 border border-white/10 px-2 py-1 w-12 focus:border-[#00E5FF] outline-none" /></td>
                <td className="p-2 text-right">
                  <button onClick={() => del(i)} className="text-gray-500 hover:text-red-400" data-testid={`delete-circuit-${i}`}><Trash size={16} /></button>
                </td>
              </tr>
            ))}
            {circuits.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-gray-500 text-sm">
                No circuits yet — click <span className="text-[#00E5FF]">Add circuit</span> or import a CSV.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
